import { AREAS, type Area, CEO_AREA, isOfficialArea } from '@data-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/features/auth/auth-context';
import { useMe } from '@/features/auth/use-me';
import { useUpdateSelf } from '@/features/profile/use-profile';
import { useTheme } from '@/features/theme/theme-context';
import { handleApiError } from '@/lib/handle-error';

/**
 * Sentinel for the "no area" select option. Distinct from the empty string
 * so radix Select (which forbids `value=""`) treats it as a real choice.
 */
const UNSET_AREA = '__unset__' as const;
type AreaFormValue = Area | typeof UNSET_AREA;

export const Route = createFileRoute('/_authed/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { user } = useAuth();
  const me = useMe();
  const { theme, toggleTheme } = useTheme();
  const update = useUpdateSelf();

  const [displayName, setDisplayName] = useState('');
  const [area, setArea] = useState<AreaFormValue>(UNSET_AREA);
  const [dirty, setDirty] = useState(false);
  // Slack opt-out mirrors the server value; updated optimistically on toggle so
  // the switch feels instant while the PATCH + ['me'] invalidation settle.
  const [slackEnabled, setSlackEnabled] = useState(true);

  // Keep the form in sync with the source of truth on first load and after
  // mutations (`me` is invalidated, fresh data flows in). Legacy areas
  // outside AREAS are preserved on the user doc but represented as
  // `UNSET_AREA` in the form so the Select renders cleanly. We only write
  // back when the admin actually picks something new.
  useEffect(() => {
    if (!me.data || dirty) return;
    setDisplayName(me.data.displayName ?? user?.displayName ?? '');
    setArea(isOfficialArea(me.data.area) ? me.data.area : UNSET_AREA);
    setSlackEnabled(me.data.slackNotificationsEnabled);
  }, [me.data, user?.displayName, dirty]);

  const handleToggleSlack = (next: boolean) => {
    const prev = slackEnabled;
    setSlackEnabled(next); // optimistic
    update.mutate(
      { slackNotificationsEnabled: next },
      {
        onSuccess: () => toast.success(next ? 'Slack notifications on' : 'Slack notifications off'),
        onError: (err) => {
          setSlackEnabled(prev); // revert
          handleApiError(err, 'Failed to update Slack notifications');
        },
      },
    );
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (area === CEO_AREA && !me.data?.canPickCeoArea) {
      toast.error('The CEO area is restricted.');
      return;
    }
    update.mutate(
      {
        displayName: displayName.trim() || undefined,
        area: area === UNSET_AREA ? null : area,
      },
      {
        onSuccess: () => {
          toast.success('Profile updated');
          setDirty(false);
        },
        onError: (err) => handleApiError(err, 'Failed to update profile'),
      },
    );
  };

  const initials = getInitials(user?.displayName, user?.email);
  const role = me.data?.role ?? 'user';

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Your name and area show up next to your activity.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 space-y-0">
          <Avatar className="size-16">
            {user?.photoURL && <AvatarImage src={user.photoURL} alt="" />}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{user?.displayName ?? user?.email}</CardTitle>
            <CardDescription className="truncate font-mono text-xs">{user?.email}</CardDescription>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={role === 'admin' ? 'default' : 'secondary'} className="capitalize">
                {role}
              </Badge>
              {me.data?.area && <Badge variant="outline">{me.data.area}</Badge>}
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Edit profile</CardTitle>
          <CardDescription>
            Display name + area are visible to everyone in the hub. Email and role are managed by an
            administrator.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {me.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <form className="grid gap-4" onSubmit={handleSave}>
              <div className="grid gap-2">
                <Label htmlFor="displayName">Display name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="Maria López"
                  maxLength={120}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="area">Area</Label>
                <Select
                  value={area}
                  onValueChange={(v) => {
                    setArea(v as AreaFormValue);
                    setDirty(true);
                  }}
                >
                  <SelectTrigger id="area" className="w-full">
                    <SelectValue placeholder="Pick an area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNSET_AREA}>— no area —</SelectItem>
                    {AREAS.map((a) => {
                      const disabled = a === CEO_AREA && !me.data?.canPickCeoArea;
                      return (
                        <SelectItem key={a} value={a} disabled={disabled}>
                          {a}
                          {disabled ? ' (restricted)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {me.data?.area && !isOfficialArea(me.data.area) && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Your stored area "{me.data.area}" is from a legacy import. Pick from the list
                    above to align with the canonical area set.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Used for resource visibility — `area` and `multi-area` resources gate by this.
                </p>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={update.isPending || !dirty}>
                  {update.isPending && <Loader2Icon className="size-4 animate-spin" />}
                  Save changes
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notifications</CardTitle>
          <CardDescription>
            Metric Hub events (reviews, decisions, comments, mentions) always show in your in-app
            bell. Optionally mirror them to Slack as direct messages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {me.isPending ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="slack-dm" className="text-sm font-medium">
                  Slack direct messages
                </Label>
                <p className="text-xs text-muted-foreground">
                  {slackEnabled
                    ? 'You get a Slack DM for every Metric Hub notification.'
                    : "You won't get Slack DMs — only the in-app bell."}
                </p>
              </div>
              <Switch
                id="slack-dm"
                checked={slackEnabled}
                onCheckedChange={handleToggleSlack}
                disabled={update.isPending}
                aria-label="Slack direct messages for Metric Hub notifications"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preferences</CardTitle>
          <CardDescription>Stored in your browser, applied on every visit.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Theme</p>
            <p className="text-xs text-muted-foreground">
              Currently {theme === 'dark' ? 'dark' : 'light'} mode.
            </p>
          </div>
          <Button variant="outline" onClick={toggleTheme}>
            Switch to {theme === 'dark' ? 'light' : 'dark'} mode
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  const source = name?.trim() || email?.split('@')[0] || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  const single = parts[0] ?? '?';
  return single.slice(0, 2).toUpperCase();
}
